import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { GalleryDto, GalleryTheme, Section } from '@nusign/picflow-backend-types';
import { SwitchComponent } from '../../../components/switch/switch.component';
import { GROWTHBOOK } from '../../../growthbook';
import { debounce } from '../../../lib/debounce';
import { assetSectionLayouts, assetSectionSizes, assetSectionSpacings } from '../../../lib/default-asset-section';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { SectionService } from '../../../services/section.service';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { CommonSectionSettingsComponent } from '../common-section-settings/common-section-settings.component';

@Component({
  selector: 'app-assets-section-menu',
  templateUrl: './assets-section-menu.component.html',
  styleUrls: ['./assets-section-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, SvgComponent, ButtonDirective, CommonSectionSettingsComponent, SwitchComponent],
})
export class AssetsSectionMenuComponent {
  public growthBook = inject(GROWTHBOOK);
  private sectionService = inject(SectionService);
  private analytics = inject(PICFLOW_ANALYTICS);
  @Input() section: Section;
  @Input() gallery: GalleryDto;
  @Input() theme: GalleryTheme;
  assetSectionValues = {
    spacings: assetSectionSpacings,
    sizes: assetSectionSizes,
    layouts: assetSectionLayouts,
  };

  updateLayout() {
    this.sectionService.updateSection(this.section, { grid_layout: this.section.grid_layout }).subscribe();
  }

  updateAssetSize(size: number) {
    this.sectionService.optimisticUpdateSection(this.section, { display_size: size });
    this.saveSize(size);
  }

  updateAssetSpacing(spacing: number) {
    this.sectionService.optimisticUpdateSection(this.section, { spacing: spacing });
    this.saveSpacing(spacing);
  }

  updateShowFilenames(showFilenames: boolean) {
    this.sectionService.updateSection(this.section, { show_filenames: showFilenames }).subscribe();
    this.analytics.track('updated_show-filenames', false, { showFilenames });
  }

  updateRoundedCorners(roundedCorners: boolean) {
    this.sectionService.updateSection(this.section, { rounded_corners: roundedCorners }).subscribe();
    this.analytics.track('updated_rounded-corners', false, { roundedCorners });
  }

  private saveSpacing = debounce((spacing: number) => {
    this.sectionService.updateSection(this.section, { spacing: spacing }).subscribe();
    this.analytics.track('updated_asset-spacing');
  }, 500);

  private saveSize = debounce((size: number) => {
    this.sectionService.updateSection(this.section, { display_size: size }).subscribe();
    this.analytics.track('updated_asset-size');
  }, 500);
}
